<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { afterUpdate, createEventDispatcher, onMount, setContext } from 'svelte';
	import { format } from 'svelte-i18n';

	import { replace } from 'svelte-spa-router';

	import {
		resetWizardItems,
		setWizardSteps,
		updateWizardStepOnPrevNext,
		updateWizardStepOnWizardItem,
		requestEditDevice,
		requestEditSharedDevice,
		scrollToElement,
		scrollToElementInConditionalStep,
	} from '../../Lists/list-helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	import {
		Modal,
		Button,
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
		toast,
		Alert,
	} from '@Shelly/shelly-design-system';

	import MainStep from './AddEditSteps/MainStep.svelte';
	import SelectRoomStep from './AddEditSteps/SelectRoomStep.svelte';
	import SelectGatewaysStep from './AddEditSteps/SelectGatewaysStep.svelte';

	// Create temporary store that will go trough context

	import { writable } from 'svelte/store';
	import { readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_local_ip,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { localReq, postReq } from 'src/services/http';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getComponentByDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	import { syncPeripheralDeviceName } from 'src/helpers/addons/addon_settings_helpers';
	import { shouldUseSysNameSync } from 'src/helpers/device_name_sync_helpers';

	let createNewRoomStore: SvelteStore<any> = writable({
		newRoom: undefined,
		newRoomEditing: false,
	});

	setContext('newRoomStore', createNewRoomStore);
	//Encapsulating createNewRoomStore into context in AddDeviceWizard and EditDeviceWizard so after the wizards are destroyed, the stores will be destroyed and unsubscribe from them.
	//the tricky part here is how we update the store
	//we have to  update the store like so:
	//$createNewRoomStore = {
	// 	newRoom: newRoomVar,
	// 	newRoomEditing: booleanVar
	// }

	// end of temporary store

	const dispatch = createEventDispatcher();

	export let editDevice;
	export let showModalEdit;
	export let showModalEditFromNotification = false;

	afterUpdate(() => {
		if (showModalEditFromNotification) {
			showModalEditFromNotification = false;
			onClickWizardItem(1);
			showModalEdit = true;
		}
	});

	onMount(() => {
		// Ivo: Doing this so we reset the selected img from upload an image
		if (editDevice?.backgroundColor !== $devices[editDevice?.id]?.backgroundColor) {
			editDevice.backgroundColor = $devices[editDevice.id].backgroundColor;
		}

		if (editDevice?.image !== $devices[editDevice?.id]?.image) {
			editDevice.image = $devices[editDevice?.id]?.image;
		}

		if (editDevice?.icon !== $devices[editDevice?.id]?.icon) {
			editDevice.icon = $devices[editDevice?.id]?.icon;
		}
	});

	const previousButton = $format('button_back');

	let wizardItemsContainerRef: HTMLElement = null;
	let wizardContainerRef: HTMLElement = null;
	let initiallyLoadedEditWizard = false;
	let selectedRef: HTMLElement = null;
	let modalRef: Modal = null;
	let requestGatewayLoader = false;
	let scrolledToView = false;
	let requestLoader = false;
	let SelectGatewaysStepComponent;

	let isNewRoomCreated = false;
	let updateFullHeight: boolean = false;
	let newRoomEditing;

	$: ({ newRoomEditing, newRoom } = $createNewRoomStore);

	let wizardSteps = [
		$format('select_room_step', { default: 'select room' }),
		$format('details_step', { default: 'details' }),
	];

	if (editDevice.gen == 'GBLE') {
		if (!editDevice.shared) {
			wizardSteps.splice(1, 0, $format('select_gateways_step', { default: 'select gateways' }));
		}
	}

	let wizardItems = setWizardSteps(wizardSteps);

	let activeWizardStep = wizardItems[0];

	$: isLastStepWizard = activeWizardStep.activeIndex === wizardItems.length - 1;
	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');

	let gatewayDisableNext = false;
	$: disableNext =
		!editDevice?.name ||
		editDevice?.name?.trim() === '' ||
		(activeWizardStep.activeIndex === 1 && editDevice.gen == 'GBLE' && gatewayDisableNext);
	$: disableLastStep =
		editDevice?.image === '' &&
		editDevice?.icon === '' &&
		editDevice?.backgroundColor === '' &&
		isLastStepWizard;
	// reset scrolled to view variable after step change
	$: (activeWizardStep.activeIndex, (scrolledToView = false));

	//#region Wizard scroll to after room created
	$: (isNewRoomCreated, scrollToElementInConditionalStep(selectedRef, isNewRoomCreated));
	//#end region Wizard scroll to after room created

	const onCloseEditModal = () => {
		showModalEdit = false;
		updateFullHeight = undefined;
		isNewRoomCreated = false;
		wizardItems = resetWizardItems(wizardItems);
		activeWizardStep = wizardItems[0];
		$createNewRoomStore = {
			newRoom: undefined,
			newRoomEditing: false,
		};
	};

	//#region Wizard Functions
	const onClickWizardItem = (index: number) => {
		// handle "Gatways step" call on Steps navigation Click
		if (editDevice.gen == 'GBLE' && index == 2 && activeWizardStep.activeIndex == 1) {
			SelectGatewaysStepComponent.updateGatewaysList();
		}

		const result = updateWizardStepOnWizardItem(index, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		// handle "Gatways Step" call on Steps navigation Next
		if (
			editDevice.gen == 'GBLE' &&
			type == 'next' &&
			activeWizardStep.activeIndex == 1 &&
			!editDevice.shared
		) {
			SelectGatewaysStepComponent.updateGatewaysList();
		}

		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onFinish = async () => {
		editDevice.name = editDevice.name.trim();
		if (editDevice.name !== '') {
			requestLoader = true;

			if (editDevice.gen !== 'GBLE') {
				const settings = readDeviceSettings(editDevice.id);
				const status = readDeviceStatus(editDevice.id);
				const isOffline = !status?.online || typeof settings === 'undefined'; //needed to prevent sending requests to the server when the device is offline. Mostly used for gen2 devices with sync name enabled

				editDevice.name_sync = $devices[editDevice.id]['name_sync'];
				editDevice.name_sync_target = $devices[editDevice.id]['name_sync_target'];

				if (/_(2|12|22)\d{2}$/.test(editDevice.id) && !editDevice.shared) {
					const trv = Object.keys(settings || {}).filter((i) => /^(blutrv):2\d{2}$/.test(i));
					for (const component in status) {
						if (!settings?.[component]) {
							continue;
						} else if (/^(group|bthomedevice):2\d{2}$/.test(component)) {
							if (
								(editDevice.channel >= 200 &&
									component != getComponentByDevice(editDevice)) ||
								trv.find((i) => settings[i]?.trv == component)
							) {
								continue;
							}
							devices_settings.update_devices_settings(real_id(editDevice.id), {
								...(readDeviceSettings(real_id(editDevice.id)) ?? {}),
								[component]: {
									...(readDeviceSettings(real_id(editDevice.id))?.[component] ?? {}),
									name: editDevice.name,
								},
							});
						}
					}
					delete editDevice.name_sync;
					toast.open({
						type: 'success',
						title: $format('virtual_device_name_scc_toast', {
							default: 'Virtual device name changed to {name}.',
							values: { name: editDevice.name },
						}),
					});
				} else if (!editDevice.shared && isPeripheralDevice(editDevice.type)) {
					syncPeripheralDeviceName(editDevice);
				}

				try {
					if (editDevice.name_sync) {
						let cacheName = settings.name;
						let isGen2 = is_gen_2(editDevice);

						if (cacheName !== editDevice.name) {
							if (isGen2) {
								if (shouldUseSysNameSync(editDevice)) {
									// sync app name to sys.device.name
									const params = {
										id: editDevice.channel,
										config: { device: { name: editDevice.name } },
									};
									const res: SettingRequest = isOffline
										? { isok: true }
										: await gen2Request(editDevice, 'Sys.SetConfig', params);

									if (res.isok) {
										devices.update(editDevice);
										let int_res = await postReq('interface/device/save', {
											id: editDevice.id,
											data: JSON.stringify(editDevice),
										});
										if (int_res.isok) {
										} else {
											toast.open({
												type: 'danger',
												title: $format('error_global'),
											});
										}

										toast.open({
											type: 'success',
											title:
												$format('device_name_scc_toast', {
													values: { value: editDevice.name },
												}) || `Device name changed to ${editDevice.name}`,
										});
										if (settings?.sys?.device) {
											settings.sys.device.name = editDevice.name;
											devices_settings.update_devices_settings(
												editDevice.id,
												settings,
											);
										}
									} else {
										toast.open({
											type: 'danger',
											title: $format('device_name_err_toast', {
												default: 'Cloud not change device name.',
											}),
										});
									}
								} else {
									// sync app name to component/channel name
									const params = {
										id: editDevice.channel,
										config: { name: editDevice.name },
									};
									const method = `${get_gen_2_component_from_category(editDevice)}.SetConfig`;
									const res: SettingRequest = isOffline
										? { isok: true }
										: await gen2Request(editDevice, method, params);

									if (res.isok) {
										toast.open({
											type: 'success',
											title:
												$format('device_name_scc_toast', {
													values: { value: editDevice.name },
												}) || `Device name changed to ${editDevice.name}`,
										});
									} else {
										toast.open({
											type: 'danger',
											title: $format('device_name_err_toast', {
												default: 'Cloud not change device name.',
											}),
										});
									}
								}
							} else {
								const id = real_id(editDevice.id);
								let localIp = get_local_ip(id);
								let res;

								if (editDevice.channels_count > 1) {
									if (localIp) {
										res = await localReq(editDevice, `${localIp}/settings`, 'post', {
											name: editDevice.name,
											channel:
												editDevice.type !== 'SHEM-3'
													? editDevice.channel
													: editDevice,
										});
									} else {
										res = await postReq(`/device/rename`, {
											id: id,
											name: editDevice.name.length > 0 ? editDevice.name : 'null',
											channel:
												editDevice.type !== 'SHEM-3'
													? editDevice.channel
													: editDevice.channel,
										});
									}
								} else {
									if (localIp) {
										res = await localReq(editDevice, `${localIp}/settings`, 'post', {
											name: editDevice.name,
										});
									} else {
										res = await postReq(`/device/rename`, {
											id: id,
											name: editDevice.name.length > 0 ? editDevice.name : 'null',
										});
									}
								}

								if (res.isok) {
									toast.open({
										type: 'success',
										title:
											$format('device_name_scc_toast', {
												values: { value: editDevice.name },
											}) || `Device name changed to ${editDevice.name}`,
									});
								} else {
									toast.open({
										type: 'danger',
										title: $format('device_name_err_toast', {
											default: 'Cloud not change device name.',
										}),
									});
								}
							}
						}

						if (is_gen_2(editDevice) && settings && !shouldUseSysNameSync(editDevice)) {
							const componentKey = get_gen_2_component_channel_structure(editDevice);
							if (settings[componentKey]) {
								settings[componentKey]['name'] = editDevice.name;
								devices_settings.update_devices_settings(editDevice.id, settings);
							}
						} else if (settings && !is_gen_2(editDevice)) {
							const category =
								editDevice.category !== 'inputs_reader'
									? editDevice.category + 's'
									: 'inputs';
							let channel = 0; /*for SHEM-3 only*/

							settings[category][
								editDevice.type !== 'SHEM-3' ? editDevice.channel : channel
							].name = editDevice.name;

							if (
								// checked &&
								editDevice.channels_count < 2
							) {
								settings.name = editDevice.name;
								devices.update(editDevice);
								let int_res = await postReq('interface/device/save', {
									id: editDevice.id,
									data: JSON.stringify(editDevice),
								});
								if (int_res.isok) {
									devices_settings.update_devices_settings(editDevice.id, settings);
									toast.open({
										type: 'success',
										title:
											$format('chan_name_succ', {
												values: { name: editDevice.name },
											}) || `Channel name was changed to ${editDevice.name}`,
									});
								} else {
									requestLoader = false;
									toast.open({
										type: 'danger',
										title: $format('error_global'),
									});
								}
							}
						}
					}
				} catch (error) {
					console.error('Failed to "Syn Name"! Error:', error);
				}
			}

			if (editDevice.shared) {
				await requestEditSharedDevice(editDevice);
			} else {
				await requestEditDevice(editDevice);
			}

			requestLoader = false;
			// Manually update items list because on edit mode items doesn't update from store, dnd flicker issue
			// setItemsEditChanges();

			// TO DO chek does this will stay.
			// Redirect ot device new room
			if (editDevice.shared && editDevice.room_id == -20) {
				replace(`/home/devices/-20`);
			} else {
				replace(`/home/room/${editDevice.room_id}/devices`);
			}

			console.log('END OF EDIT DEVICE');
			dispatch('finish');
			onCloseEditModal();
		}
	};
	//#end region Wizard Functions

	let removeContainerEventListener = () => {};

	//#region Wizard Scroll to Functions
	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		let elementRef = null;
		let wizardStepIndex = 0;

		let scrollIndex = editDevice.gen == 'GBLE' ? 2 : 0;
		if (activeWizardStep.activeIndex === scrollIndex && selectedRef && !scrolledToView) {
			elementRef = selectedRef;
			wizardStepIndex = 0;

			scrolledToView = true;
		}

		if (!elementRef) {
			// Scroll to top, when no card is selected
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeWizardStep.activeIndex,
				wizardStepIndex,
			);
		}
	};

	$: if (!initiallyLoadedEditWizard && selectedRef && wizardContainerRef) {
		scrollToElement(selectedRef, wizardContainerRef, 0, 0);
		initiallyLoadedEditWizard = true;
	}
	//#end region Wizard Scroll to Functions
</script>

<Modal
	on:close={onCloseEditModal}
	showModal={showModalEdit}
	title={$format('prompt_title_edit_device', {
		default: 'Edit device?',
	})}
	bind:this={modalRef}
	hideSlotFooter={newRoomEditing}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#if !newRoomEditing}
			{#each wizardItems as item, i (i)}
				<WizardItem
					on:click={() => {
						onClickWizardItem(i);
					}}
					class="text-capitalize"
					active={item.active}
					title={item.title}
					disabled={item.disabled ||
						(editDevice.name === '' && !item.active) ||
						(newRoomEditing && !item.active) ||
						(editDevice.gen == 'GBLE' && i === 2 && gatewayDisableNext)}
					showMoreDots={item.showMoreDots}
					indicatorColor={$showNewLayout ? 'color-2' : undefined}
				/>
			{/each}
		{:else}
			<WizardItem
				active={true}
				class="text-capitalize"
				title={$format('button_card_add_room', { default: 'Add room' })}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/if}
	</Wizard>

	<div class:flex-overflow-hidden={newRoomEditing} slot="content">
		<WizardContainer
			adaptiveHeight
			activeStepIndex={activeWizardStep.activeIndex}
			bind:wizardItemsContainerRef
			bind:wizardContainerRef
			fullHeight={updateFullHeight}
		>
			{#each wizardItems as _, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent
					active={activeWizardStep.activeIndex === i}
					class={classMap({
						'pb-s pt-s': true,
						'pl-0 pr-0': i == 1 && editDevice.gen == 'GBLE' && !editDevice.shared,
					})}
				>
					{#if i === 0}
						<SelectRoomStep
							bind:editDevice
							bind:selectedRef
							bind:updateFullHeight
							on:after-room-update={() => {
								isNewRoomCreated = true;
							}}
							{requestLoader}
						/>
					{:else if i == 1 && editDevice.gen == 'GBLE' && !editDevice.shared}
						<SelectGatewaysStep
							bind:requestGatewayLoader
							bind:this={SelectGatewaysStepComponent}
							associatedDevice={editDevice}
							bind:gatewayDisableNext
						/>
					{:else}
						<MainStep bind:editDevice on:enter={() => onClickPrevNextButton('next')} />
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<svelte:fragment slot="footer">
		{#if activeWizardStep.activeIndex === 1 && editDevice.gen == 'GBLE' && gatewayDisableNext}
			<Alert
				class="mb-s"
				heading={capFirstChar(
					$format('alert_header_danger_no_gateway_selection', {
						default: 'Please select at least one gateway.',
					}),
				)}
				isHeadingBold
				type={'danger'}
			/>
		{/if}
		<div class="d-flex justify-content-end">
			{#if activeWizardStep.activeIndex > 0}
				<Button
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
					icon="fas fa-chevron-left"
					variant="secondary"
					responsiveSize={{
						sm: 'm',
					}}
					on:click={() => onClickPrevNextButton('prev')}
					disabled={requestLoader || requestGatewayLoader}
				>
					{previousButton}
				</Button>
			{/if}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				disabled={disableNext || requestLoader || requestGatewayLoader || disableLastStep}
				icon={isLastStepWizard ? 'fas fa-save' : ''}
				rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => {
					if (isLastStepWizard) {
						onFinish();
					} else {
						onClickPrevNextButton('next');
					}
				}}
			>
				{nextButton}
			</Button>
		</div>
	</svelte:fragment>
</Modal>
